import React, { useEffect, useState } from 'react';
import {
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Card,
  CardContent,
  Typography,
  Box,
  InputAdornment,
  IconButton,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Field, Form, Formik } from 'formik';
import InputMask from 'react-input-mask';
import PersonIcon from '@material-ui/icons/Person';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import StorefrontIcon from '@material-ui/icons/Storefront';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import LockIcon from '@material-ui/icons/Lock';

import validationSchema from './validationSchema';
import UIContainer from '../../UI/Container/UIContainer';
import UIFormContainerSave from '../../UI/Form/ContainerSave/UIFormContainer';
import useApi from '../../../utils/useApi';
import useStorage from '../../../utils/useStorage';
import UIModalOldForm from '../../UI/ModalOldForm/UIModalOldForm';
import GreenSwitch from '../../UI/Form/Switch/GreenSwitch';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#f4f6f8',
    minHeight: '100vh',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  card: {
    borderRadius: 16,
    boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.05)',
    marginBottom: theme.spacing(3),
    overflow: 'hidden',
  },
  cardHeader: {
    padding: theme.spacing(2, 3),
    borderBottom: '1px solid #f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
  },
  cardTitle: {
    fontWeight: 600,
    fontSize: '1.1rem',
    color: theme.palette.text.primary,
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1),
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  inputField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
    },
  },
}));

const initialState = {
  name: '',
  lastName: '',
  cpf: '',
  username: '',
  password: '',
  confirmPassword: '',
  cashRegisters: [],
  isActive: true,
  profile: '1',
};

const UserNew = () => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [oldValues, setOldValues] = useStorage('formUsers');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [registerLoad, registerInfo, setRegisterInfo] = useApi({
    method: 'post',
    url: '/users',
  });

  const [cashierLoad, cashierInfo] = useApi({
    method: 'get',
    url: '/cash-registers',
    params: { isActive: '1' },
  });

  const [profileLoad, profileInfo] = useApi({
    method: 'get',
    url: '/users/profiles',
  });

  function changeModal() {
    setOpen(!open);
  }

  function setFormValues() {
    setOldValues(null);
    changeModal();
  }

  useEffect(() => {
    cashierLoad();
    profileLoad();
    if (
      oldValues &&
      JSON.stringify(oldValues) !== JSON.stringify(initialState)
    ) {
      changeModal();
    }
  }, []);

  function onSubmit(values) {
    const { confirmPassword, ...rest } = values;
    const data = {
      ...rest,
      username: values.username ? values.username.trim().toLowerCase() : '',
      isActive: values.isActive ? 1 : '0',
      cashRegisters: values.cashRegisters.map((value) => value.id),
    };
    registerLoad({
      data,
    });
  }

  return (
    <div className={classes.root}>
      <Formik
        enableReinitialize
        initialValues={oldValues || initialState}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {({ setFieldValue, values, errors, touched }) => (
          <Form onBlur={() => setOldValues(values)}>
            <UIContainer>
              <Card className={classes.card}>
                <Box className={classes.cardHeader}>
                  <Box display="flex" alignItems="center">
                    <PersonIcon color="primary" style={{ marginRight: 12 }} />
                    <Typography className={classes.cardTitle}>
                      Novo Usuário
                    </Typography>
                  </Box>
                  <FormControlLabel
                    id="tour-user-active"
                    label={
                      <Typography
                        variant="body2"
                        style={{
                          fontWeight: 600,
                          color: values.isActive ? '#2e7d32' : '#757575',
                        }}
                      >
                        {values.isActive ? 'Ativo' : 'Inativo'}
                      </Typography>
                    }
                    labelPlacement="start"
                    control={
                      <GreenSwitch
                        checked={values.isActive}
                        onChange={(ev) =>
                          setFieldValue('isActive', ev.target.checked)
                        }
                      />
                    }
                  />
                </Box>
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography className={classes.sectionTitle}>
                        Dados Pessoais
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Field
                        id="tour-user-name"
                        name="name"
                        label="Nome"
                        variant="outlined"
                        className={classes.inputField}
                        error={Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                        placeholder="Informe o nome"
                        fullWidth
                        as={TextField}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        name="lastName"
                        label="Sobrenome"
                        variant="outlined"
                        className={classes.inputField}
                        error={Boolean(errors.lastName)}
                        helperText={touched.lastName && errors.lastName}
                        placeholder="Informe o sobrenome"
                        fullWidth
                        as={TextField}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InputMask
                        value={values.cpf}
                        mask="999.999.999-99"
                        onChange={(ev) =>
                          setFieldValue(
                            'cpf',
                            ev.target.value.replace(/[^0-9]/g, ''),
                          )
                        }
                      >
                        {() => (
                          <TextField
                            id="tour-user-cpf"
                            label="CPF"
                            variant="outlined"
                            className={classes.inputField}
                            error={Boolean(errors.cpf)}
                            helperText={touched.cpf && errors.cpf}
                            placeholder="Informe o CPF"
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AssignmentIndIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      </InputMask>
                    </Grid>

                    <Grid item xs={12} style={{ marginTop: 16 }}>
                      <Typography className={classes.sectionTitle}>
                        Dados de Acesso e Permissões
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={12}>
                      <Field
                        id="tour-user-login"
                        name="username"
                        error={Boolean(errors.username)}
                        helperText={touched.username && errors.username}
                        label="Login"
                        variant="outlined"
                        className={classes.inputField}
                        placeholder="Informe o login"
                        fullWidth
                        as={TextField}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AccountCircleIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        id="tour-user-password"
                        name="password"
                        error={Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                        type={showPassword ? 'text' : 'password'}
                        label="Senha"
                        variant="outlined"
                        className={classes.inputField}
                        placeholder="Informe a senha"
                        fullWidth
                        as={TextField}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <VpnKeyIcon color="action" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                              >
                                {showPassword ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        name="confirmPassword"
                        error={Boolean(errors.confirmPassword)}
                        helperText={
                          touched.confirmPassword && errors.confirmPassword
                        }
                        type={showConfirmPassword ? 'text' : 'password'}
                        label="Confirmar Senha"
                        variant="outlined"
                        className={classes.inputField}
                        placeholder="Confirme a senha"
                        fullWidth
                        as={TextField}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockIcon color="action" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                                edge="end"
                              >
                                {showConfirmPassword ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl
                        id="tour-user-profile"
                        fullWidth
                        variant="outlined"
                        className={classes.inputField}
                      >
                        <InputLabel shrink id="users-new-gender">
                          Perfil
                        </InputLabel>
                        <Field
                          name="profile"
                          disabled={profileInfo.loading}
                          error={Boolean(errors.profile)}
                          helperText={touched.profile && errors.profile}
                          labelId="users-new-gender"
                          displayEmpty
                          fullWidth
                          as={Select}
                          label="Perfil"
                        >
                          <MenuItem disabled value="">
                            <em>Informe o perfil</em>
                          </MenuItem>
                          {profileInfo.data &&
                            profileInfo.data.map((prof) => (
                              <MenuItem key={prof.id} value={prof.id}>
                                {prof.name}
                              </MenuItem>
                            ))}
                        </Field>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        id="tour-user-cashier"
                        multiple
                        disabled={cashierInfo.loading}
                        options={cashierInfo.data || []}
                        value={values.cashRegisters}
                        getOptionLabel={(cash) => cash.name}
                        onChange={(event, cash) =>
                          setFieldValue('cashRegisters', cash)
                        }
                        getOptionSelected={(op, cash) => op.name === cash.name}
                        noOptionsText="Sem opções"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            className={classes.inputField}
                            error={Boolean(errors.cashRegisters)}
                            helperText={
                              touched.cashRegisters && errors.cashRegisters
                            }
                            label="Operadores de Caixa"
                            placeholder="Selecione os caixas"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <StorefrontIcon color="action" />
                                  </InputAdornment>
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <UIFormContainerSave
                id="tour-user-save"
                container
                justify="center"
                direction="row"
                messageSucess="Usuário cadastrado com sucesso"
                messageError="Alguma coisa está errada, verifique os campos"
                error={registerInfo.error}
                loading={registerInfo.loading || ''}
                data={registerInfo.data}
                setRegisterInfo={setRegisterInfo}
                setFormValues={() => setOldValues(null)}
              />

              <UIModalOldForm
                open={open}
                changeModal={changeModal}
                setFormValues={setFormValues}
              />
            </UIContainer>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default UserNew;